// src/components/JiraKanbanView.tsx
import { useEffect, useMemo, useState, useCallback } from "react";
import "../JiraKanbanView.css";
import "../JiraKanbanOverrides.css";
import ColumnSettings from "../ColumnSettings";
import TaskDetailsSidebar from "../tasks/TaskDetailsSidebar";
import TaskCard from "../tasks/TaskCard";
import type { ID, Task, TaskStatus, Priority, Label, UserProfile, Project, Company } from "../../types/domain";
import { useTasksQuery } from "../../hooks/useTasksQuery";
import { createTask } from "../../services/tasks";
import { listCompanyLabels, getLabelsForTasks, replaceTaskLabels } from "../../services/labels";
import { getSubtaskStatsForTasks } from "../../services/subtasks";
import { searchProfiles } from "../../services/users";
import { loadBoardConfig, saveBoardConfig, resetBoardConfig, type ColumnConfig } from "../../lib/boardConfig";
import { toast } from "react-hot-toast";
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
  DropAnimation,
} from "@hello-pangea/dnd";
import { 
  FiPlus
} from "react-icons/fi";
import { useSupabase } from "../../hooks/useSupabase";
import GlobalSidebar from "../layout/GlobalSidebar";
import GlobalHeader from "../layout/GlobalHeader";

type Props = { 
  companyId?: ID; 
  projectId?: ID; 
};


const STATUS_LABEL: Record<TaskStatus, string> = {
  backlog: "Backlog",
  todo: "To do",
  in_progress: "In progress",
  in_review: "In review",
  done: "Done",
  archived: "Archivadas/Eliminadas",
  deleted: "Eliminada",
};

const STATUS_ICON: Record<TaskStatus, string> = {
  backlog: "📦",
  todo: "📝",
  in_progress: "🛠️",
  in_review: "🔎",
  done: "✅",
  archived: "🗃️",
  deleted: "🗑️",
};

export default function JiraKanbanView({ companyId, projectId }: Props) {
  const { supabase } = useSupabase();
  const [search, setSearch] = useState("");
  const [boardConf, setBoardConf] = useState<ColumnConfig[]>(loadBoardConfig(projectId));
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Data states
  const [projects, setProjects] = useState<Project[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [allLabels, setAllLabels] = useState<Label[]>([]);
  const [labelMap, setLabelMap] = useState<Map<ID, Label[]>>(new Map());
  const [subStats, setSubStats] = useState<Map<ID, { total: number; done: number }>>(new Map());

  // Filter states
  const [selectedProjectId, setSelectedProjectId] = useState<ID | "">(projectId || "");
  const [selectedCompanyId, setSelectedCompanyId] = useState<ID | "">(companyId || "");
  const [priorityFilter, setPriorityFilter] = useState<Priority | "">("");
  const [labelFilter, setLabelFilter] = useState<ID[]>([]);
  const [assigneeFilter, setAssigneeFilter] = useState<ID | "">("");

  // UI states
  const [openTask, setOpenTask] = useState<Task | null>(null);

  // Load all projects and companies for navigation
  const loadProjects = useCallback(async () => {
    if (!supabase) return;
    try {
      const { data: projectsData } = await supabase
        .from('projects')
        .select('*')
        .order('name');
      setProjects(projectsData || []);

      const { data: companiesData } = await supabase
        .from('companies')
        .select('*')
        .order('name');
      setCompanies(companiesData || []);
    } catch (error) {
      console.error('Error loading projects and companies:', error);
    }
  }, [supabase]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Tasks query - load tasks based on current filters
  const { tasks, loading, error, setFilters, reload } = useTasksQuery({
    projectId: selectedProjectId || undefined,
    search: search.trim() || undefined,
    orderBy: "position",
    ascending: true,
    limit: 2000,
  });

  // Keep query in sync with sidebar filters
  useEffect(() => {
    setFilters({
      projectId: selectedProjectId || undefined,
      search: (search || "").trim() || undefined,
    });
  }, [selectedProjectId, search, setFilters]);

  const [viewTasks, setViewTasks] = useState<Task[]>([]);
  useEffect(() => setViewTasks(tasks ?? []), [tasks]);

  const visibleStatusOrder = boardConf.filter((c) => c.visible).map((c) => c.key);

  const isFiltering = 
    priorityFilter !== "" ||
    labelFilter.length > 0 ||
    assigneeFilter !== "" ||
    (search ?? "").trim().length > 0;

  // Load labels for selected company
  useEffect(() => {
    if (!selectedCompanyId) {
      setAllLabels([]);
      return;
    }
    (async () => {
      const { data } = await listCompanyLabels(selectedCompanyId);
      setAllLabels(data);
    })();
  }, [selectedCompanyId]);

  // Load labels and subtask stats for current tasks
  useEffect(() => {
    const ids = (viewTasks ?? []).map((t) => t.id);
    (async () => {
      setLabelMap(await getLabelsForTasks(ids));
      setSubStats(await getSubtaskStatsForTasks(ids));
    })();
  }, [viewTasks]);

  // Update board config when projectId changes
  useEffect(() => {
    setBoardConf(loadBoardConfig(selectedProjectId));
  }, [selectedProjectId]);

  // Cleanup state and emergency handlers for drag
  useEffect(() => {
    const emergencyCleanup = () => {
      document.body.classList.remove("dragging");
      document.body.style.removeProperty('pointer-events');
      
      // Clean up any stuck drag visuals
      document.querySelectorAll('[data-dragging="true"]').forEach(el => {
        el.removeAttribute('data-dragging');
      });
      
      document.querySelectorAll('.kb-card').forEach(el => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.style.transform && (htmlEl.style.transform.includes('rotate') || htmlEl.style.transform.includes('scale'))) {
          htmlEl.style.transform = '';
        }
        if (htmlEl.style.opacity && htmlEl.style.opacity !== '1') {
          htmlEl.style.opacity = '';
        }
      });
    };
    
    // Emergency cleanup on various events
    document.addEventListener('visibilitychange', emergencyCleanup);
    window.addEventListener('blur', emergencyCleanup);
    window.addEventListener('focus', emergencyCleanup);
    document.addEventListener('mouseup', emergencyCleanup);
    document.addEventListener('touchend', emergencyCleanup);
    
    return () => {
      document.removeEventListener('visibilitychange', emergencyCleanup);
      window.removeEventListener('blur', emergencyCleanup);
      window.removeEventListener('focus', emergencyCleanup);
      document.removeEventListener('mouseup', emergencyCleanup);
      document.removeEventListener('touchend', emergencyCleanup);
      emergencyCleanup();
    };
  }, []);

  // Filter tasks based on current filters
  const visibleTasks = useMemo(() => {
    let list = (viewTasks ?? []).slice();
    // Filter by selected company via project mapping
    if (selectedCompanyId) {
      const allowed = new Set((projects || []).filter(p => p.company_id === selectedCompanyId).map(p => p.id));
      list = list.filter((t) => allowed.has(t.project_id));
    }
    if (priorityFilter) list = list.filter((t) => t.priority === priorityFilter);
    if (assigneeFilter) list = list.filter((t) => (t.assignee_id ?? "") === assigneeFilter);
    if (labelFilter.length > 0) {
      list = list.filter((t) => {
        const labels = labelMap.get(t.id) ?? [];
        const ids = new Set(labels.map((l) => l.id));
        return labelFilter.every((id) => ids.has(id));
      });
    }
    return list;
  }, [viewTasks, selectedCompanyId, projects, priorityFilter, assigneeFilter, labelFilter, labelMap]);

  const grouped = useMemo(() => groupByStatus(visibleTasks), [visibleTasks]);

  const clearFilters = () => {
    setPriorityFilter("");
    setAssigneeFilter("");
    setLabelFilter([]);
    setSearch("");
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    // No destination means the item was dropped outside a droppable area
    if (!destination) {
      return;
    }

    // Same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    // Update task status if moving between columns
    if (destination.droppableId !== source.droppableId) {
      try {
        await supabase
          .from('tasks')
          .update({ status: destination.droppableId })
          .eq('id', draggableId);
        
        await reloadAll();
      } catch (error) {
        console.error('Error updating task status:', error);
      }
    }
  };

  // Hook personalizado para detectar tamaño de ventana
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Función para determinar el layout de las tareas en base al número y tamaño de ventana
  const getTasksContainerClass = (tasksCount: number) => {
    // Calcular el ancho disponible para las columnas del Kanban
    const availableWidth = windowSize.width - 320; // Restar sidebar (320px)
    
    // Estimar cuántas columnas del Kanban caben en pantalla
    const columnWidth = 320; // Ancho mínimo de columna Kanban + gap
    const maxVisibleColumns = Math.floor(availableWidth / columnWidth);
    
    // Si tenemos muy pocas tareas, usar una columna independientemente del tamaño de pantalla
    if (tasksCount <= 4) return 'jira-tasks-container';
    
    // Para pantallas muy pequeñas (móviles), siempre usar una columna
    if (windowSize.width < 768) return 'jira-tasks-container';
    
    // Para pantallas medianas
    if (windowSize.width < 1200) {
      // Si hay espacio para pocas columnas o hay pocas tareas, usar 1 columna
      if (maxVisibleColumns <= 2 || tasksCount <= 8) return 'jira-tasks-container';
      // Si hay muchas tareas y espacio, usar 2 columnas
      return 'jira-tasks-container multi-column';
    }
    
    // Para pantallas grandes
    if (windowSize.width < 1600) {
      if (tasksCount <= 6) return 'jira-tasks-container';
      if (tasksCount <= 15) return 'jira-tasks-container multi-column';
      return 'jira-tasks-container three-column';
    }
    
    // Para pantallas muy grandes
    if (tasksCount <= 8) return 'jira-tasks-container';
    if (tasksCount <= 18) return 'jira-tasks-container multi-column';
    return 'jira-tasks-container three-column';
  };


  async function reloadAll() {
    await reload();
    const ids = (tasks ?? []).map((x) => x.id);
    setLabelMap(await getLabelsForTasks(ids));
    setSubStats(await getSubtaskStatsForTasks(ids));
  }

  const dropAnim: DropAnimation = {
    duration: 180,
    curve: "ease-in-out",
    moveToNewContainer: true,
  };

  const handleProjectSelect = (newProjectId: ID | "") => {
    setSelectedProjectId(newProjectId);
    if (newProjectId) {
      // Find the project's company and set it
      const project = projects.find(p => p.id === newProjectId);
      if (project && project.company_id) {
        setSelectedCompanyId(project.company_id);
      }
    }
  };

  return (
    <>
      {/* Global Header */}
      <GlobalHeader
        selectedProjectId={selectedProjectId}
        selectedCompanyId={selectedCompanyId}
        projects={projects}
        companies={companies}
        viewType="kanban"
        onSettingsClick={() => setSettingsOpen(true)}
        onCreateTask={() => {
          // Activar QuickAdd para la primera columna visible
          const firstVisibleStatus = visibleStatusOrder.find(status => status !== "archived");
          if (firstVisibleStatus) {
            const element = document.querySelector(`[data-status="${firstVisibleStatus}"] .quick-add-input`);
            if (element) {
              (element as HTMLInputElement).focus();
            }
          }
        }}
        onUserClick={() => {
          // Aquí puedes agregar funcionalidad del usuario
        }}
      />
      
      {/* Global Sidebar */}
      <GlobalSidebar
        selectedProjectId={selectedProjectId}
        selectedCompanyId={selectedCompanyId}
        priorityFilter={priorityFilter}
        assigneeFilter={assigneeFilter}
        searchTerm={search}
        onProjectChange={handleProjectSelect}
        onCompanyChange={setSelectedCompanyId}
        onPriorityChange={setPriorityFilter}
        onAssigneeChange={setAssigneeFilter}
        onSearchChange={setSearch}
        onClearFilters={clearFilters}
        projects={projects}
        companies={companies}
        tasks={tasks}
        showFilters={true}
        showProjectStats={true}
      />

      <div className="jira-kanban-container">
        {/* Main Content */}
        <div className="jira-main">

        {loading && (
          <div className="jira-loading">
            Cargando tareas...
          </div>
        )}

        {error && <div className="jira-error">⚠️ {error}</div>}

        {/* Kanban Board */}
        {isFiltering ? (
          <div className="jira-kanban-board">
            {visibleStatusOrder.map((status) => (
              <section key={status} className={`jira-kanban-column jira-${status}`}>
                <header className="jira-column-header">
                  <div className="jira-column-header-left">
                    <div className="jira-column-title">
                      <span className="jira-column-icon">{STATUS_ICON[status]}</span>
                      <h2>{headerName(status)}</h2>
                    </div>
                  </div>
                  <div className="jira-column-header-right">
                    <span className="jira-column-count">{(grouped[status] || []).length}</span>
                    {selectedProjectId && status !== "archived" && (
                      <button 
                        className="add-task-btn-header"
                        onClick={() => {
                          // Activar QuickAdd para esta columna
                          const element = document.querySelector(`[data-status="${status}"] .quick-add-input`);
                          if (element) {
                            (element as HTMLInputElement).focus();
                          }
                        }}
                        title="Crear nueva tarea"
                      >
                        <FiPlus size={16} />
                      </button>
                    )}
                  </div>
                </header>
                {selectedProjectId && status !== "archived" && (
                  <QuickAdd
                    status={status}
                    projectId={selectedProjectId!}
                    allLabels={allLabels}
                    onCreated={async (tempId, realId) => {
                      setViewTasks((prev) =>
                        prev.map((t) => (t.id === tempId ? ({ ...t, id: realId } as Task) : t))
                      );
                      await reloadAll();
                    }}
                    onOptimistic={(draft) => {
                      setViewTasks((prev) => [draft, ...prev]);
                    }}
                    onRollback={(tempId) =>
                      setViewTasks((prev) => prev.filter((t) => t.id !== tempId))
                    }
                  />
                )}
                <div className={getTasksContainerClass((grouped[status] || []).length)}>
                  {(grouped[status] || []).map((t) => (
                    <TaskCard
                      key={t.id}
                      task={t}
                      labels={labelMap.get(t.id) ?? []}
                      subStat={subStats.get(t.id) ?? { total: 0, done: 0 }}
                      allLabels={allLabels}
                      openDetail={() => setOpenTask(t)}
                      reload={reloadAll}
                    />
                  ))}
                </div>
                {(grouped[status] || []).length === 0 && (
                  <div className="jira-empty-state">No hay tareas</div>
                )}
              </section>
            ))}
          </div>
        ) : (
          <DragDropContext
            onDragStart={() => {
              document.body.classList.add("dragging");
            }}
            onDragEnd={(result) => {
              // Aggressive cleanup of all drag-related visual state
              document.body.classList.remove("dragging");
              
              // Force cleanup of any stuck drag elements
              const cleanup = () => {
                document.querySelectorAll('[data-dragging="true"]').forEach(el => {
                  el.removeAttribute('data-dragging');
                });
                
                document.querySelectorAll('.kb-card').forEach(el => {
                  const htmlEl = el as HTMLElement;
                  const currentTransform = htmlEl.style.transform;
                  if (currentTransform && (currentTransform.includes('rotate') || currentTransform.includes('scale'))) {
                    htmlEl.style.transform = currentTransform
                      .replace(/rotate\([^)]*\)/g, '')
                      .replace(/scale\([^)]*\)/g, '')
                      .trim() || '';
                  }
                  if (htmlEl.style.opacity && htmlEl.style.opacity !== '1') {
                    htmlEl.style.opacity = '';
                  }
                });
                
                document.body.classList.remove("dragging");
              };
              
              cleanup();
              setTimeout(cleanup, 100);
              setTimeout(cleanup, 300);
              
              void handleDragEnd(result);
            }}
            dropAnimation={dropAnim}
          >
            <div className="jira-kanban-board">
              {visibleStatusOrder.map((status) => (
                <Droppable droppableId={status} key={status} type="TASK" ignoreContainerClipping>
                  {(dropProvided, dropSnapshot) => (
                    <section
                      className={`jira-kanban-column jira-${status} ${dropSnapshot.isDraggingOver ? 'is-over' : ''}`}
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                    >
                      <header className="jira-column-header">
                        <div className="jira-column-header-left">
                          <div className="jira-column-title">
                            <span className="jira-column-icon">{STATUS_ICON[status]}</span>
                            <h2>{headerName(status)}</h2>
                          </div>
                        </div>
                        <div className="jira-column-header-right">
                          <span className="jira-column-count">{(grouped[status] || []).length}</span>
                          {selectedProjectId && status !== "archived" && (
                            <button 
                              className="add-task-btn-header"
                              onClick={() => {
                                // Activar QuickAdd para esta columna
                                const element = document.querySelector(`[data-status="${status}"] .quick-add-input`);
                                if (element) {
                                  (element as HTMLInputElement).focus();
                                }
                              }}
                              title="Crear nueva tarea"
                            >
                              <FiPlus size={16} />
                            </button>
                          )}
                        </div>
                      </header>


                      {selectedProjectId && status !== "archived" && (
                        <QuickAdd
                          status={status}
                          projectId={selectedProjectId!}
                          allLabels={allLabels}
                          onCreated={async (tempId, realId) => {
                            setViewTasks((prev) =>
                              prev.map((t) =>
                                t.id === tempId ? ({ ...t, id: realId } as Task) : t
                              )
                            );
                            await reloadAll();
                          }}
                          onOptimistic={(draft) => {
                            setViewTasks((prev) => [draft, ...prev]);
                          }}
                          onRollback={(tempId) =>
                            setViewTasks((prev) => prev.filter((t) => t.id !== tempId))
                          }
                        />
                      )}

                      <div className={`${getTasksContainerClass((grouped[status] || []).length)} ${dropSnapshot.isDraggingOver ? 'is-over' : ''}`}>
                        {(grouped[status] || []).map((t, index) => (
                          <Draggable draggableId={t.id} index={index} key={t.id}>
                            {(dragProvided, snap) => (
                              <TaskCard
                                task={t}
                                labels={labelMap.get(t.id) ?? []}
                                subStat={subStats.get(t.id) ?? { total: 0, done: 0 }}
                                allLabels={allLabels}
                                openDetail={() => setOpenTask(t)}
                                reload={reloadAll}
                                provided={dragProvided}
                                snapshot={snap}
                              />
                            )}
                          </Draggable>
                        ))}
                        {dropProvided.placeholder}
                      </div>

                      {(grouped[status] || []).length === 0 && (
                        <div className="jira-empty-state">No hay tareas</div>
                      )}
                    </section>
                  )}
                </Droppable>
              ))}
            </div>
          </DragDropContext>
        )}
      </div>

      {/* Column Settings Modal */}
      <ColumnSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        value={boardConf}
        onChange={(val) => {
          setBoardConf(val);
          saveBoardConfig(selectedProjectId, val);
        }}
        onReset={() => {
          resetBoardConfig(selectedProjectId);
          const conf = loadBoardConfig(selectedProjectId);
          setBoardConf(conf);
        }}
      />

      {/* Task Details Sidebar */}
      <TaskDetailsSidebar
        task={openTask}
        labels={openTask ? labelMap.get(openTask.id) ?? [] : []}
        allLabels={allLabels}
        isOpen={!!openTask}
        onClose={() => setOpenTask(null)}
        onTaskUpdated={reloadAll}
        onTaskDeleted={async () => { setOpenTask(null); await reloadAll(); }}
      />

      </div>
    </>
  );

  function headerName(status: TaskStatus) {
    return boardConf.find((c) => c.key === status)?.name ?? STATUS_LABEL[status];
  }
}

/* ===== QuickAdd (crear inline) ===== */
function QuickAdd({
  status,
  projectId,
  allLabels,
  onCreated,
  onOptimistic,
  onRollback,
}: {
  status: TaskStatus;
  projectId: ID;
  allLabels: Label[];
  onCreated: (tempId: ID, realId: ID) => Promise<void>;
  onOptimistic: (draft: Task) => void;
  onRollback: (tempId: ID) => void;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState<Priority | "">("");
  const [deadline, setDeadline] = useState("");
  const [assigneeId, setAssigneeId] = useState<ID | "">("");
  const [selectedLabels, setSelectedLabels] = useState<ID[]>([]);
  
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Cargar usuarios cuando se abre el formulario
  useEffect(() => {
    if (open && users.length === 0) {
      setLoadingUsers(true);
      searchProfiles("").then(({ data }) => {
        setUsers(data || []);
        setLoadingUsers(false);
      });
    }
  }, [open, users.length]);

  if (!open) {
    return (
      <div data-status={status}>
        <button
          className="jira-add-task-btn-icon"
          onClick={() => setOpen(true)}
          aria-label="Añadir tarea"
          title="Añadir tarea"
        >
          <FiPlus size={16} />
        </button>
      </div>
    );
  }

  return (
    <form
      className="jira-add-task-form"
      data-status={status}
      onSubmit={async (e) => {
        e.preventDefault();
        const t = title.trim();
        if (!t) return;
        setSaving(true);

        // Optimistic update
        const tempId = `tmp_${Math.random().toString(36).slice(2)}` as ID;
        const draft: Task = {
          id: tempId,
          project_id: projectId,
          title: t,
          description: desc.trim() || null,
          priority: (priority || null) as Priority | null,
          deadline: deadline || null,
          status,
          assignee_id: (assigneeId || null) as ID | null,
          position: 1,
          created_at: new Date().toISOString(),
          user_id: null,
          owner_id: null,
        } as unknown as Task;
        onOptimistic(draft);

        const { error, data } = await createTask({
          project_id: projectId,
          title: t,
          description: draft.description,
          priority: draft.priority,
          deadline: draft.deadline,
          status,
          assignee_id: draft.assignee_id,
        });

        if (error || !data?.id) {
          toast.error("No se pudo crear la tarea");
          onRollback(tempId);
          setSaving(false);
          return;
        }
        if (selectedLabels.length > 0) await replaceTaskLabels(data.id, selectedLabels);

        toast.success("Tarea creada");
        setSaving(false);
        setTitle("");
        setDesc("");
        setPriority("");
        setDeadline("");
        setAssigneeId("");
        setSelectedLabels([]);
        setOpen(false);
        await onCreated(tempId, data.id);
      }}
    >
      <input
        className="jira-task-input quick-add-input"
        placeholder="Título de la tarea"
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        disabled={saving}
      />
      <textarea
        className="jira-task-textarea"
        rows={2}
        placeholder="Descripción (opcional)..."
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        disabled={saving}
      />

      <div className="jira-task-form-row">
        <label className="jira-form-field">
          <span>Prioridad</span>
          <select
            className="jira-select"
            value={priority}
            onChange={(e) => setPriority((e.target.value as Priority | "") || "")}
            disabled={saving}
          >
            <option value="">Sin prioridad</option>
            <option value="low">Baja</option>
            <option value="medium">Media</option>
            <option value="high">Alta</option>
            <option value="urgent">Urgente</option>
          </select>
        </label>
        <label className="jira-form-field">
          <span>Fecha límite</span>
          <input
            type="date"
            className="jira-task-input"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            disabled={saving}
          />
        </label>
      </div>

      <label className="jira-form-field">
        <span>Asignado a</span>
        <select
          className="jira-select"
          value={assigneeId}
          onChange={(e) => setAssigneeId(e.target.value as ID)}
          disabled={saving || loadingUsers}
        >
          <option value="">Sin asignar</option>
          {loadingUsers && <option value="">Cargando usuarios...</option>}
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.full_name || user.email || `Usuario ${user.id.slice(0, 8)}`}
            </option>
          ))}
        </select>
      </label>

      {allLabels.length > 0 && (
        <div className="jira-form-field">
          <span>Etiquetas</span>
          {selectedLabels.length > 0 && (
            <div className="jira-selected-labels">
              {selectedLabels.map((labelId) => {
                const label = allLabels.find(l => l.id === labelId);
                if (!label) return null;
                return (
                  <span
                    key={label.id}
                    className="jira-label-tag"
                    style={{ background: label.color || "var(--jira-blue)" }}
                  >
                    {label.name}
                    <button
                      type="button"
                      className="jira-label-remove"
                      onClick={() => setSelectedLabels(prev => prev.filter(id => id !== labelId))}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <div className="jira-labels-picker">
            {allLabels.map((l) => (
              <label key={l.id} className="jira-label-checkbox">
                <input
                  type="checkbox"
                  checked={selectedLabels.includes(l.id)}
                  onChange={() =>
                    setSelectedLabels((prev) =>
                      prev.includes(l.id)
                        ? prev.filter((x) => x !== l.id)
                        : [...prev, l.id]
                    )
                  }
                  disabled={saving}
                />
                <span
                  className="jira-label-swatch"
                  style={{ background: l.color || "var(--jira-blue)" }}
                />
                <span className="jira-label-name">{l.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="jira-task-form-actions">
        <button className="jira-button jira-button-primary" type="submit" disabled={saving}>
          {saving ? "Creando..." : "Crear"}
        </button>
        <button 
          className="jira-button jira-button-secondary" 
          type="button" 
          onClick={() => setOpen(false)} 
          disabled={saving}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

function groupByStatus(list: Task[] | undefined) {
  const base: Record<TaskStatus, Task[]> = {
    backlog: [],
    todo: [],
    in_progress: [],
    in_review: [],
    done: [],
    archived: [],
    deleted: [],
  };
  (list ?? []).forEach((t) => {
    const key = t.status === "deleted" ? "archived" : t.status;
    (base[key] ?? base.backlog).push(t);
  });
  (Object.keys(base) as TaskStatus[]).forEach((k) => {
    base[k].sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id.localeCompare(b.id));
  });
  return base;
}
